import { worldCountries } from '@/data/countries/index';
import { getCityBySlug } from '@/data/cities';
import { citySpecialties } from '@/data/citySpecialties';

export interface URLValidationResult {
  totalCities: number;
  workingCities: string[];
  broken404Cities: string[];
  totalSpecialties: number;
  workingSubLandings: number;
  broken404SubLandings: string[];
}

/**
 * Validates all city URLs and specialty sub-landing URLs
 * Returns detailed statistics about working and broken URLs
 */
export const validateAllCityURLs = (): URLValidationResult => {
  const results: URLValidationResult = {
    totalCities: 0,
    workingCities: [],
    broken404Cities: [],
    totalSpecialties: citySpecialties.length,
    workingSubLandings: 0,
    broken404SubLandings: []
  };
  
  // Iterate through all countries and their cities
  worldCountries.forEach(country => {
    country.topCities.forEach(city => {
      results.totalCities++;
      
      // Check if city exists in the system (can be found by slug)
      const cityExists = getCityBySlug(city.slug);
      
      // Also check in worldCountries data structure directly
      const cityExistsInCountries = worldCountries.some(c => 
        c.topCities.some(tc => tc.slug === city.slug)
      );
      
      if (cityExists || cityExistsInCountries) {
        results.workingCities.push(city.slug);
        
        // Verify specialty sub-landings for this city
        citySpecialties.forEach(specialty => {
          // All working cities should have all specialty sub-landings available
          results.workingSubLandings++;
        });
      } else {
        results.broken404Cities.push(city.slug);
        
        // If city is broken, all its specialty sub-landings are also broken
        citySpecialties.forEach(specialty => {
          results.broken404SubLandings.push(`${city.slug}/${specialty.slugs.en}`);
        });
      }
    });
  });
  
  return results;
};

/**
 * Get a sample of broken URLs for debugging
 */
export const getBrokenURLSample = (maxSamples: number = 10): string[] => {
  const results = validateAllCityURLs();
  return results.broken404Cities.slice(0, maxSamples);
};

/**
 * Check if a specific city URL is valid
 */
export const isCityURLValid = (citySlug: string): boolean => {
  const cityExists = getCityBySlug(citySlug);
  if (cityExists) return true;
  
  // Fallback: check in worldCountries
  return worldCountries.some(country => 
    country.topCities.some(city => city.slug === citySlug)
  );
};

/**
 * Check if a specific specialty sub-landing URL is valid
 */
export const isSpecialtyURLValid = (citySlug: string, specialtySlug: string): boolean => {
  // First check if city exists
  if (!isCityURLValid(citySlug)) return false;
  
  // Then check if specialty exists
  return citySpecialties.some(specialty => 
    specialty.slugs.en === specialtySlug || 
    Object.values(specialty.slugs).includes(specialtySlug)
  );
};

/**
 * Get validation statistics summary
 */
export const getValidationStats = () => {
  const results = validateAllCityURLs();
  
  return {
    cities: {
      total: results.totalCities,
      working: results.workingCities.length,
      broken: results.broken404Cities.length,
      percentage: results.totalCities > 0 
        ? (results.workingCities.length / results.totalCities * 100).toFixed(2)
        : '0.00'
    },
    subLandings: {
      total: results.totalCities * results.totalSpecialties,
      working: results.workingSubLandings,
      broken: results.broken404SubLandings.length,
      percentage: (results.totalCities * results.totalSpecialties) > 0
        ? (results.workingSubLandings / (results.totalCities * results.totalSpecialties) * 100).toFixed(2)
        : '0.00'
    },
    summary: {
      totalURLs: results.totalCities + (results.totalCities * results.totalSpecialties),
      workingURLs: results.workingCities.length + results.workingSubLandings,
      brokenURLs: results.broken404Cities.length + results.broken404SubLandings.length
    }
  };
};
